// 🔧 pages/admin/vendor.js
// ✅ Purpose: Admin/Manager page to add or update vendor info
// ✅ Features:
// - Works in Add mode or Edit mode
// - Loads existing vendor if editing
// - Saves vendors to /events/{eventId}/vendors
// - Preserves old logo unless replaced
// - Uploads logos to /eventAssets/{eventId}/vendors/
// - Supports upload progress bar
// - Supports QR code printing for public vendor page
// - Supports vendor category, booth number, featured vendor, and contact email
// - Supports event map marker placement with blue vendor marker
// - Shows 20s success/error alerts
// - Fully block-commented for clarity and future devs

import React, { useState, useEffect, useRef } from "react";
import { useEventContext } from "../../hooks/use_event_context";
import { db, storage, functions } from "../../firebase";
import {
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  collection,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref,
  deleteObject,
} from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import { useParams, useNavigate, Link } from "react-router-dom";
import { logger } from "../../utils/logger";
import { useUploadWithProgress } from "../../hooks/use_upload_with_progress";
import { showStatus } from "../../utils/status_helpers";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import QRCode from "react-qr-code";
import { httpsCallable } from "firebase/functions";
import {
  hasPurchasedAddOn,
} from "../../utils/event_addons";
import { resolvePowerupSnapshot } from "../../utils/powerup_urls";
import PromotionSourceButton from "../../promotion/components/promotion_source_button";

const VendorForm = () => {

  const {
    eventId,
    eventDoc,
  } = useEventContext();
  const { eventslug, id: vendorId } = useParams();
  const navigate = useNavigate();

  const { uploadFileWithProgress, progress } = useUploadWithProgress();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    vendorSlug: "",
    description: "",
    url: "",
    logoFile: null,
    logoUrl: "",
    category: "",
    boothNumber: "",
    vendorContactEmail: "",
    featuredVendor: false,
    // ✅ Vendor payment fields
    // ✅ These mirror sponsor payments and support future Stripe checkout
    paymentRequired: false,
    amountCents: 0,
    paymentStatus: "not_required",
    approvalStatus: "approved",
    isApproved: true,
    mapMarkerLabel: "",
    mapMarkerTitle: "",
    mapMarkerNote: "",
    mapMarkerType: "vendor",
    mapMarkerColor: "primary",
    mapMarkerX: "",
    mapMarkerY: "",
  });

  const [vendorCategories, setVendorCategories] = useState([]);
  const [allVendors, setAllVendors] = useState([]);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resolvedVendorId, setResolvedVendorId] = useState("");
  const vendorRecordId = resolvedVendorId || vendorId;
  const [eventMap, setEventMap] = useState("");
  const [imageInfo, setImageInfo] = useState({
    width: null,
    height: null,
    sizeKB: null,
  });

  // =====================================================
  // 💳 Vendor Payment Add-on Gate
  // =====================================================
  // ✅ Purpose:
  // - Vendor listings are included in the core LGV Event Package
  // - Only online/manual Vendor Payment workflows require the Vendor Payments add-on
  // - Uses purchasedAddOns.vendorPayments as the source of truth
  // =====================================================
  const vendorPaymentsEnabled = hasPurchasedAddOn(
    eventDoc,
    "vendorPayments"
  );

  useEffect(() => {
    if (!status?.message) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  const resetVendorForm = () => {
    setResolvedVendorId("");
    setForm({
      name: "",
      vendorSlug: "",
      description: "",
      url: "",
      logoFile: null,
      logoUrl: "",
      category: "",
      boothNumber: "",
      vendorContactEmail: "",
      featuredVendor: false,
      // ✅ Vendor payment defaults
      paymentRequired: false,
      amountCents: 0,
      paymentStatus: "not_required",
      approvalStatus: "approved",
      isApproved: true,
      mapMarkerLabel: "",
      mapMarkerTitle: "",
      mapMarkerNote: "",
      mapMarkerType: "vendor",
      mapMarkerColor: "primary",
      mapMarkerX: "",
      mapMarkerY: "",
    });

    setImageInfo({
      width: null,
      height: null,
      sizeKB: null,
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const buildVendorSlug = (name = "") =>
    String(name)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

    // =====================================================
    // 🔤 Vendor Public URL Helper
    // Purpose:
    // - Ensures all public vendor links use the vendor name slug
    // - Falls back to document ID only if a slug/name is unavailable
    // =====================================================

    const loadVendor = async () => {
    if (!eventId || !vendorId) return;

    try {
        const snap = await resolvePowerupSnapshot(db, eventId, "vendor", vendorId);

        if (snap?.exists()) {
        const data = snap.data();
        setResolvedVendorId(snap.id);

        setForm((prev) => ({
            ...prev,
            name: data.name || "",
            vendorSlug: data.vendorSlug || "",
            description: data.description || "",
            url: data.url || "",
            logoUrl: data.logoUrl || "",
            category: data.category || "",
            boothNumber: data.boothNumber || "",
            vendorContactEmail: data.vendorContactEmail || "",
            featuredVendor: data.featuredVendor === true,
            paymentRequired: data.paymentRequired === true,
            amountCents: Number(data.amountCents || 0),
            paymentStatus: data.paymentStatus || "not_required",
            approvalStatus: data.approvalStatus || "approved",
            isApproved: data.isApproved !== false,
            mapMarkerLabel: data.mapMarkerLabel || "",
            mapMarkerTitle: data.mapMarkerTitle || "",
            mapMarkerNote: data.mapMarkerNote || "",
            mapMarkerType: data.mapMarkerType || "vendor",
            mapMarkerColor: data.mapMarkerColor || "primary",
            mapMarkerX: data.mapMarkerX ?? "",
            mapMarkerY: data.mapMarkerY ?? "",
        }));
        }
    } catch (err) {
        logger.error("❌ Error loading vendor:", {
        value: err,
        });

        showStatus(setStatus, {
        type: "error",
        message: "Failed to load vendor.",
        });
    }
    };

    const loadAllVendors = async () => {
    if (!eventId) return;

    try {
        const vendorsSnap = await getDocs(
        collection(db, "events", eventId, "vendors")
        );

        const list = vendorsSnap.docs
        .map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
        }))
        .sort((a, b) =>
            String(a.name || "").localeCompare(
            String(b.name || ""),
            undefined,
            {
                sensitivity: "base",
            }
            )
        );

        setAllVendors(list);
    } catch (err) {
        logger.error("❌ Error loading saved vendors:", {
        value: err,
        });

        setAllVendors([]);
    }
    };

    useEffect(() => {
        if (!eventId) return;

        loadAllVendors();

        if (vendorId) {
            loadVendor();
        } else {
            setResolvedVendorId("");
            resetVendorForm();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventId, vendorId]);

  useEffect(() => {
    if (!eventId) return;

    const loadEventMapAndCategories = async () => {
      try {
        const settingsRef = doc(db, "events", eventId, "settings", "voting");
        const snap = await getDoc(settingsRef);
        const data = snap.exists() ? snap.data() : {};

        setEventMap(data.eventMap || "");

        if (Array.isArray(data.vendorCategories)) {
          setVendorCategories(
            data.vendorCategories.map((category) => ({
              name: category.name || "",
              paymentRequired: category.paymentRequired === true,
              amountCents: Number(category.amountCents || 0),
            }))
          );
        }

      } catch (err) {
        logger.warn("⚠️ Could not load vendor settings", {
          value: err,
        });

        setEventMap("");
        setVendorCategories([]);
      }
    };

    loadEventMapAndCategories();
  }, [eventId]);

  useEffect(() => {
    if (!form.logoUrl) {
      setImageInfo({
        width: null,
        height: null,
        sizeKB: null,
      });

      return;
    }

    const img = new Image();
    img.src = form.logoUrl;

    img.onload = async () => {
      let sizeKB = null;

      try {
        const res = await fetch(form.logoUrl);
        const blob = await res.blob();

        sizeKB = (blob.size / 1024).toFixed(1);
      } catch (err) {
        logger.warn("⚠️ Failed to fetch vendor logo size:", {
          value: err,
        });
      }

      setImageInfo({
        width: img.naturalWidth,
        height: img.naturalHeight,
        sizeKB,
      });
    };
  }, [form.logoUrl]);

  const handleChange = (e) => {
    const { name, value, files, checked, type } = e.target;

    if (name === "logoFile") {
      setForm((prev) => ({
        ...prev,
        logoFile: files[0],
      }));

      return;
    }

    if (type === "checkbox") {
      setForm((prev) => ({
        ...prev,
        [name]: checked,
      }));

      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRemoveLogo = async () => {
    if (!form.logoUrl) return;

    try {
      const path = new URL(form.logoUrl).pathname;

      const cleanPath = decodeURIComponent(
        path.replace(/^\/v0\/b\/[^/]+\/o\//, "")
      );

      const oldRef = ref(storage, cleanPath);

      await deleteObject(oldRef);

      logger.log("🧹 Vendor logo manually removed", {
        value: cleanPath,
      });

      setForm((prev) => ({
        ...prev,
        logoUrl: "",
        logoFile: null,
      }));

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      showStatus(setStatus, {
        type: "warning",
        message: "Logo removed. You can upload a new one.",
      });
    } catch (err) {
      logger.warn("⚠️ Error removing vendor logo:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Error removing logo.",
      });
    }
  };

  const handleVendorPaymentOverride = async ({
    paymentStatus,
    paymentMethod,
    approvalStatus = "approved",
    isApproved = true,
  }) => {
    const vendorRecordId = resolvedVendorId || vendorId;
    if (!eventId || !vendorRecordId) return;

    const note = window.prompt(
      "Optional note for this payment/approval override:",
      ""
    );

    try {
      const vendorRef = doc(db, "events", eventId, "vendors", vendorRecordId);

      await updateDoc(vendorRef, {
        paymentRequired: form.paymentRequired === true,
        paymentStatus,
        approvalStatus,
        isApproved,
        paymentMethod,
        manualApprovalNote: note || "",
        approvedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setForm((prev) => ({
        ...prev,
        paymentStatus,
        approvalStatus,
        isApproved,
      }));

      showStatus(setStatus, {
        type: "success",
        message: "Vendor payment/approval status updated.",
      });
    } catch (err) {
      logger.error("❌ Failed to update vendor payment override:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to update vendor payment/approval status.",
      });
    }
  };

  // Retry against the saved recipient; form email/content never define delivery authority.
  const handleSendVendorNotice = async () => {
    if (saving || !eventId || !vendorRecordId) return;
    setSaving(true);
    try {
      const { data } = await httpsCallable(functions, "sendVendorContactNotice")({ eventId, vendorId: vendorRecordId });
      showStatus(setStatus, { type: "success", message: data.simulated
        ? "Demo vendors do not send external email."
        : data.sending ? "Vendor notification is already being sent."
        : data.alreadySent ? "A valid vendor verification email has already been sent."
        : "Vendor verification email sent successfully." });
    } catch (error) {
      showStatus(setStatus, { type: "error", message: error?.message || "Unable to send vendor verification email. Please retry." });
    } finally { setSaving(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (saving) return;

    if (!String(form.name || "").trim() || !eventId) {
      showStatus(setStatus, {
        type: "error",
        message: "Vendor name is required.",
      });

      return;
    }

    setSaving(true);

    try {
      let logoUrl = form.logoUrl || null;

      if (form.logoFile) {
        if (form.logoUrl) {
          try {
            const path = new URL(form.logoUrl).pathname;

            const cleanPath = decodeURIComponent(
              path.replace(/^\/v0\/b\/[^/]+\/o\//, "")
            );

            const oldRef = ref(storage, cleanPath);

            await deleteObject(oldRef);

            logger.log("🧹 Deleted old vendor logo:", {
              value: cleanPath,
            });
          } catch (err) {
            logger.warn("⚠️ Failed to delete old vendor logo:", {
              value: err,
            });
          }
        }

        const fileExt = form.logoFile.name.split(".").pop();
        const filePath = `eventAssets/${eventId}/vendors/${uuidv4()}.${fileExt}`;
        const storageRef = ref(storage, filePath);

        logoUrl = await uploadFileWithProgress(storageRef, form.logoFile);

        setForm((prev) => ({
          ...prev,
          logoUrl,
        }));
      }

      const vendorPayload = {
        name: form.name.trim(),
        description: form.description || "",
        url: form.url || "",
        logoUrl,
        category: form.category || "",
        boothNumber: String(form.boothNumber || "").trim(),
        vendorContactEmail: String(form.vendorContactEmail || "").trim().toLowerCase(),
        featuredVendor: form.featuredVendor === true,

        paymentRequired: form.paymentRequired === true,
        amountCents:
          form.paymentRequired === true
            ? Number(form.amountCents || 0)
            : 0,
        paymentStatus:
          form.paymentRequired === true
            ? form.paymentStatus || "pending"
            : "not_required",
        approvalStatus:
          form.paymentRequired === true
            ? form.approvalStatus || "pending"
            : "approved",
        isApproved:
          form.paymentRequired === true
            ? form.paymentStatus === "paid" ||
              form.paymentStatus === "waived"
            : true,
        mapMarkerLabel: String(form.mapMarkerLabel || "").trim(),
        mapMarkerTitle: String(form.mapMarkerTitle || "").trim(),
        mapMarkerNote: String(form.mapMarkerNote || "").trim(),
        mapMarkerType: "vendor",
        mapMarkerColor: "primary",
        mapMarkerX:
          form.mapMarkerX === "" || form.mapMarkerX === null
            ? null
            : Number(form.mapMarkerX),
        mapMarkerY:
          form.mapMarkerY === "" || form.mapMarkerY === null
            ? null
            : Number(form.mapMarkerY),
        updatedAt: serverTimestamp(),
      };

      if (vendorId && !vendorRecordId) {
        throw new Error("Vendor route did not resolve to a record.");
      }
      if (vendorRecordId) {
        const docRef = doc(db, "events", eventId, "vendors", vendorRecordId);

        await updateDoc(docRef, vendorPayload);
        const slugResult = await httpsCallable(functions, "updatePowerupSlug")({ eventId, family: "vendor", entityId: vendorRecordId, slug: form.vendorSlug || undefined });
        setForm((previous) => ({ ...previous, vendorSlug: slugResult.data.slug }));

        showStatus(setStatus, {
          type: "success",
          message: `Updated vendor '${form.name}'.`,
        });
        loadAllVendors();
      } else {
        const newVendorRef = await addDoc(
          collection(db, "events", eventId, "vendors"),
          {
            ...vendorPayload,
            createdAt: serverTimestamp(),
          }
        );
        // Preserve saved identity if slug allocation fails; retry must not add another vendor.
        setResolvedVendorId(newVendorRef.id);
        const slugResult = await httpsCallable(functions, "updatePowerupSlug")({ eventId, family: "vendor", entityId: newVendorRef.id, slug: form.vendorSlug || undefined });
        setForm((previous) => ({ ...previous, vendorSlug: slugResult.data.slug }));

        showStatus(setStatus, {
          type: "success",
          message: `Vendor '${form.name}' added successfully. You can continue editing it here.`,
        });

        setForm((prev) => ({
          ...prev,
          logoFile: null,
          logoUrl,
        }));

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        loadAllVendors();

        navigate(`/${eventslug}/vendor/${slugResult.data.slug}`, {
          replace: true,
        });
      }
    } catch (err) {
      logger.error("🔥 Error saving vendor:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Error saving vendor.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePrintQr = () => {
    const printContent = document.getElementById("print-qr");
    const printWindow = window.open("", "", "width=800,height=800");

    if (!printWindow || !printContent) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>Print Vendor QR Code</title>
          <style>
            body {
              margin: 0;
              padding: 0;
              display: flex;
              justify-content: center;
              align-items: center;
              height: 100vh;
            }

            #qr-container {
              width: 6in;
              height: 6in;
              display: flex;
              justify-content: center;
              align-items: center;
            }

            img, svg {
              max-width: 100%;
              max-height: 100%;
            }
          </style>
        </head>

        <body>
          <div id="qr-container">
            ${printContent.innerHTML}
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const handleMapClick = (e) => {
    if (!eventMap) return;

    const rect = e.currentTarget.getBoundingClientRect();

    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setForm((prev) => ({
      ...prev,
      mapMarkerType: "vendor",
      mapMarkerColor: "primary",
      mapMarkerX: Number(x.toFixed(2)),
      mapMarkerY: Number(y.toFixed(2)),
    }));
  };

  const clearMapMarkerPlacement = () => {
    setForm((prev) => ({
      ...prev,
      mapMarkerX: "",
      mapMarkerY: "",
    }));

    showStatus(setStatus, {
      type: "warning",
      message: "Vendor map marker placement cleared.",
    });
  };

  const displayedVendorMarkers = allVendors.map((vendor) => {
  if (vendor.id !== (resolvedVendorId || vendorId)) return vendor;

  return {
        ...vendor,
        name: form.name || vendor.name,
        mapMarkerLabel: form.mapMarkerLabel,
        mapMarkerTitle: form.mapMarkerTitle,
        mapMarkerNote: form.mapMarkerNote,
        mapMarkerX: form.mapMarkerX,
        mapMarkerY: form.mapMarkerY,
    };
    });

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1200px" }}>
        <div className="row mb-4">
          <div className="col-12 col-lg-8 text-start">
            <h1 className="display-5">
              {vendorId ? "✏️ Edit Vendor" : "🛍️ Add Vendor"}
            </h1>

            <p className="lead text-muted mb-0">
              {vendorId
                ? "Update vendor details, logo, QR code, and map location."
                : "Add a vendor to this event."}
            </p>
          </div>

          <div className="col-12 col-lg-4 mt-3 mt-lg-0 d-flex flex-wrap gap-2 justify-content-lg-end align-items-start">
            <PromotionSourceButton source="vendors" sourceId={resolvedVendorId} />
            <Link
              to={`/${eventslug}/vendors_list`}
              className="btn btn-outline-secondary"
            >
              ← Back to Vendor List
            </Link>
          </div>
        </div>

        <div className="card shadow-sm border-0">
          <div className="card-body p-4">
            {status?.message && (
              <div
                className={`alert ${
                  status.type === "success"
                    ? "alert-success"
                    : status.type === "warning"
                    ? "alert-warning"
                    : status.type === "error"
                    ? "alert-danger"
                    : "alert-info"
                }`}
                role="alert"
              >
                {status.message}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="row g-4 mb-4">
                <div className="col-12 col-md-4 text-center">
                  {vendorId && eventslug && (
                    <>
                      <div
                        id="print-qr"
                        onClick={handlePrintQr}
                        className="d-inline-block"
                        style={{ cursor: "pointer" }}
                        title="Click to print QR"
                      >
                        <QRCode
                          value={`/${eventslug}/vendors/${form.vendorSlug || resolvedVendorId || vendorId}`}
                          size={250}
                        />

                        <div className="small text-muted mt-2">
                          Click QR to print
                        </div>
                      </div>

                      <div className="small text-muted mt-2">
                        Scan to view public vendor page
                      </div>
                    </>
                  )}

                  {!vendorId && (
                    <div className="alert alert-light border">
                      Save the vendor first to generate a public QR code.
                    </div>
                  )}
                </div>

                <div className="col-12 col-md-8">
                  <div className="mb-3">
                    <Link
                      to={`/${eventslug}/vendor`}
                      className="btn btn-primary"
                    >
                      ➕ Add New Vendor
                    </Link>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      Vendor Name *
                    </label>

                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Public URL slug</label>
                    <input type="text" className="form-control" name="vendorSlug" value={form.vendorSlug} onChange={handleChange} placeholder={buildVendorSlug(form.name) || "vendor-name"} />
                    <div className="form-text">Lowercase letters, numbers, and single hyphens. Leave empty to keep or create the automatic URL.</div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      Description
                    </label>

                    <ReactQuill
                      theme="snow"
                      value={form.description}
                      onChange={(val) =>
                        setForm((prev) => ({
                          ...prev,
                          description: val,
                        }))
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="row g-3 mb-4">
                <div className="col-12 col-md-6">
                  <label className="form-label fw-bold">
                    Website URL
                  </label>

                  <input
                    type="url"
                    className="form-control"
                    name="url"
                    value={form.url}
                    onChange={handleChange}
                    placeholder="https://vendor.com"
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label fw-bold">
                    Vendor Point of Contact Email
                  </label>

                  <input
                    type="email"
                    className="form-control"
                    name="vendorContactEmail"
                    value={form.vendorContactEmail}
                    onChange={handleChange}
                    placeholder="contact@vendor.com"
                  />

                  <div className="form-text">
                    Optional. New vendors with a contact email receive a private link to review and update their public information.
                  </div>
                  {vendorRecordId && <button type="button" className="btn btn-outline-primary mt-2" disabled={saving}
                    onClick={handleSendVendorNotice}>Send Vendor Verification Email</button>}
                  <div className="form-text">Save contact changes before sending a verification email.</div>
                </div>

                {vendorCategories.length > 0 && (
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-bold">
                      Vendor Category
                    </label>

                    <select
                      className="form-select"
                      name="category"
                      value={form.category}
                      onChange={(e) => {
                        const selectedCategoryName = e.target.value;

                        const selectedCategory = vendorCategories.find(
                          (category) => category.name === selectedCategoryName
                        );

                        setForm((prev) => ({
                          ...prev,
                          category: selectedCategoryName,
                          paymentRequired:
                            selectedCategory?.paymentRequired === true,
                          amountCents:
                            selectedCategory?.paymentRequired === true
                              ? Number(selectedCategory.amountCents || 0)
                              : 0,
                          paymentStatus:
                            selectedCategory?.paymentRequired === true
                              ? prev.paymentStatus === "paid" ||
                                prev.paymentStatus === "waived"
                                ? prev.paymentStatus
                                : "pending"
                              : "not_required",
                          approvalStatus:
                            selectedCategory?.paymentRequired === true
                              ? prev.approvalStatus === "approved" &&
                                (prev.paymentStatus === "paid" ||
                                  prev.paymentStatus === "waived")
                                ? "approved"
                                : "pending"
                              : "approved",
                          isApproved:
                            selectedCategory?.paymentRequired === true
                              ? prev.paymentStatus === "paid" ||
                                prev.paymentStatus === "waived"
                              : true,
                        }));
                      }}
                                          >
                      <option value="">Select category...</option>

                      {vendorCategories.map((category, idx) => (
                        <option key={idx} value={category.name}>
                          {category.name}
                          {category.paymentRequired
                            ? ` - $${(Number(category.amountCents || 0) / 100).toFixed(2)}`
                            : " - No fee"}
                        </option>
                      ))}
                      
                    </select>

                    {form.category && (
                      <div className="alert alert-light border mt-3 mb-0">
                        <div className="fw-bold">Vendor Payment Status</div>

                        {form.paymentRequired ? (
                          <div className="text-muted">
                            This vendor category requires a fee of{" "}
                            <strong>
                              ${(Number(form.amountCents || 0) / 100).toFixed(2)}
                            </strong>
                            . The vendor will stay pending until payment is received or manually approved.
                          </div>
                        ) : (
                          <div className="text-muted">
                            This vendor category does not require payment.
                          </div>
                        )}
                      </div>
                    )}

                  </div>
                )}

                <div className="col-12 col-md-6">
                  <label className="form-label fw-bold">
                    Booth Number / Location Label
                  </label>

                  <input
                    type="text"
                    className="form-control"
                    name="boothNumber"
                    value={form.boothNumber}
                    onChange={handleChange}
                    placeholder="Example: Booth 12, Food Row, Space A4"
                  />
                </div>

                <div className="col-12">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="featuredVendor"
                      name="featuredVendor"
                      checked={form.featuredVendor}
                      onChange={handleChange}
                    />

                    <label
                      className="form-check-label fw-bold"
                      htmlFor="featuredVendor"
                    >
                      Feature this vendor on Event Home
                    </label>

                    <div className="form-text">
                      Featured vendors can be highlighted in the public Event Home vendor section.
                    </div>
                  </div>
                </div>
              </div>

              <div className="card shadow-sm border-0 mb-4">
                <div className="card-header bg-white">
                  <strong>🗺️ Vendor Map Location</strong>
                </div>

                <div className="card-body">
                  <p className="text-muted mb-3">
                    Optional. Enter a printed map marker label, or click directly on the event map to place this vendor.
                  </p>

                  <div className="row g-3 mb-4">
                    <div className="col-12 col-md-4">
                      <label className="form-label fw-bold">
                        Map Marker Label
                      </label>

                      <input
                        type="text"
                        className="form-control"
                        name="mapMarkerLabel"
                        value={form.mapMarkerLabel}
                        onChange={handleChange}
                        placeholder="Example: V7"
                      />
                    </div>

                    <div className="col-12 col-md-8">
                      <label className="form-label fw-bold">
                        Map Marker Title
                      </label>

                      <input
                        type="text"
                        className="form-control"
                        name="mapMarkerTitle"
                        value={form.mapMarkerTitle}
                        onChange={handleChange}
                        placeholder="Example: Booth 7 near food court"
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label fw-bold">
                        Map Marker Note
                      </label>

                      <textarea
                        className="form-control"
                        name="mapMarkerNote"
                        value={form.mapMarkerNote}
                        onChange={handleChange}
                        rows="3"
                        placeholder="Example: Located near the main entrance beside the food trucks."
                      />
                    </div>
                  </div>

                  {eventMap ? (
                    <>
                      <div className="alert alert-info">
                        Click the map below to place this vendor. Saved vendor markers are shown in blue and can be clicked to edit that vendor. The current vendor marker is shown darker.
                      </div>

                      <div
                        className="position-relative border rounded overflow-hidden bg-light"
                        onClick={handleMapClick}
                        style={{
                          cursor: "crosshair",
                          maxWidth: "100%",
                        }}
                      >
                        <img
                          src={eventMap}
                          alt="Event Map"
                          className="img-fluid d-block w-100"
                          style={{
                            objectFit: "contain",
                          }}
                        />

                        {/* ✅ Show all saved vendor markers on the map */}
                        {displayedVendorMarkers
                        .filter(
                            (vendor) =>
                            vendor.mapMarkerX !== null &&
                            vendor.mapMarkerX !== undefined &&
                            vendor.mapMarkerX !== "" &&
                            vendor.mapMarkerY !== null &&
                            vendor.mapMarkerY !== undefined &&
                            vendor.mapMarkerY !== ""
                        )
                        .map((vendor) => {
                                const isCurrentVendor = vendor.id === (resolvedVendorId || vendorId);

                            return (
                            <Link
                                key={vendor.id}
                                to={`/${eventslug}/vendor/${vendor.vendorSlug || vendor.id}`}
                                onClick={(e) => {
                                e.stopPropagation();
                                }}
                                className={`position-absolute translate-middle rounded-circle d-flex align-items-center justify-content-center fw-bold shadow text-decoration-none ${
                                isCurrentVendor ? "bg-dark text-white" : "bg-primary text-white"
                                }`}
                                style={{
                                left: `${vendor.mapMarkerX}%`,
                                top: `${vendor.mapMarkerY}%`,
                                width: isCurrentVendor ? "42px" : "36px",
                                height: isCurrentVendor ? "42px" : "36px",
                                border: "3px solid #fff",
                                zIndex: isCurrentVendor ? 3 : 2,
                                }}
                                title={`${vendor.name || "Vendor"}${
                                vendor.mapMarkerTitle ? ` - ${vendor.mapMarkerTitle}` : ""
                                }`}
                            >
                                {vendor.mapMarkerLabel || "V"}
                            </Link>
                            );
                        })}

                        </div>

                      <div className="d-flex flex-column flex-md-row justify-content-between gap-2 mt-3">
                        <div className="small text-muted">
                          Current placement:{" "}
                          {form.mapMarkerX !== "" && form.mapMarkerY !== ""
                            ? `${form.mapMarkerX}% X, ${form.mapMarkerY}% Y`
                            : "No marker placed yet."}
                        </div>

                        {(form.mapMarkerX !== "" || form.mapMarkerY !== "") && (
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={clearMapMarkerPlacement}
                          >
                            Clear Marker Placement
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="alert alert-warning mb-0">
                      No event map has been uploaded yet. Upload the event map from Event Setup → Images first.
                    </div>
                  )}
                </div>
              </div>

              {form.logoUrl && (
                <div className="mb-3">
                  <label className="form-label fw-bold">
                    Current Logo
                  </label>

                  <div className="d-flex align-items-center gap-3">
                    <img
                      src={form.logoUrl}
                      alt="Vendor Logo"
                      style={{
                        maxHeight: "50px",
                        maxWidth: "150px",
                        border: "1px solid #ccc",
                      }}
                    />

                    <div>
                      {imageInfo.width && imageInfo.height && (
                        <div className="text-muted small mb-1">
                          {imageInfo.width}×{imageInfo.height}px
                          {imageInfo.sizeKB && ` · ${imageInfo.sizeKB} KB`}
                        </div>
                      )}

                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={handleRemoveLogo}
                        title="Remove logo"
                      >
                        ❌ Remove
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-3">
                <label className="form-label fw-bold">
                  Logo Image
                </label>

                <input
                  type="file"
                  className="form-control"
                  name="logoFile"
                  accept="image/*"
                  onChange={handleChange}
                  ref={fileInputRef}
                />
              </div>

              {progress > 0 && progress < 100 && (
                <div className="mb-3">
                  <label className="form-label">
                    Upload Progress
                  </label>

                  <div className="progress">
                    <div
                      className="progress-bar progress-bar-striped progress-bar-animated"
                      role="progressbar"
                      style={{ width: `${progress}%` }}
                      aria-valuenow={progress}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    >
                      {progress}%
                    </div>
                  </div>
                </div>
              )}

              {vendorId && !vendorPaymentsEnabled && (
                <div className="alert alert-warning">
                  Vendor Payments are not enabled for this event yet. Go to Event Package & Add-ons and purchase or enable Vendor Payments.
                </div>
              )}

              {vendorId && vendorPaymentsEnabled && (
                <div className="card shadow-sm border-0 mb-4">
                  <div className="card-header bg-white">
                    <strong>💳 Vendor Payment / Approval Override</strong>
                  </div>

                  <div className="card-body">
                    <div className="row g-3 mb-3">
                      <div className="col-12 col-md-4">
                        <div className="border rounded p-3 h-100">
                          <div className="text-muted small">Payment Required</div>
                          <div className="fw-bold">
                            {form.paymentRequired ? "Yes" : "No"}
                          </div>
                        </div>
                      </div>

                      <div className="col-12 col-md-4">
                        <div className="border rounded p-3 h-100">
                          <div className="text-muted small">Amount</div>
                          <div className="fw-bold">
                            ${(Number(form.amountCents || 0) / 100).toFixed(2)}
                          </div>
                        </div>
                      </div>

                      <div className="col-12 col-md-4">
                        <div className="border rounded p-3 h-100">
                          <div className="text-muted small">Current Status</div>
                          <div className="fw-bold">
                            {form.paymentStatus || "not_required"} /{" "}
                            {form.approvalStatus || "approved"}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="alert alert-light border">
                      Use these buttons when a vendor pays by cash/check, the fee is waived,
                      or the manager wants to manually approve the vendor.
                    </div>

                    <div className="d-flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="btn btn-outline-success"
                        onClick={() =>
                          handleVendorPaymentOverride({
                            paymentStatus: "paid",
                            paymentMethod: "cash",
                          })
                        }
                      >
                        💵 Mark Paid Cash
                      </button>

                      <button
                        type="button"
                        className="btn btn-outline-success"
                        onClick={() =>
                          handleVendorPaymentOverride({
                            paymentStatus: "paid",
                            paymentMethod: "check",
                          })
                        }
                      >
                        🧾 Mark Paid Check
                      </button>

                      <button
                        type="button"
                        className="btn btn-outline-info"
                        onClick={() =>
                          handleVendorPaymentOverride({
                            paymentStatus: "waived",
                            paymentMethod: "waived",
                          })
                        }
                      >
                        🤝 Waive Fee
                      </button>

                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() =>
                          handleVendorPaymentOverride({
                            paymentStatus: form.paymentRequired
                              ? form.paymentStatus || "paid"
                              : "not_required",
                            paymentMethod: "manual",
                          })
                        }
                      >
                        ✅ Approve Manually
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="d-flex flex-wrap gap-2">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                >
                  {saving
                    ? "Saving..."
                    : vendorId
                    ? "💾 Update Vendor"
                    : "➕ Save Vendor"}
                </button>

                <Link
                  to={`/${eventslug}/vendors_list`}
                  className="btn btn-outline-secondary"
                >
                  Cancel
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VendorForm;
